"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const AssertionError_1 = require("../error/AssertionError");
const EtudeError_1 = require("../error/EtudeError");
const CircularIterator_1 = require("../util/CircularIterator");
const Exceptional_1 = require("../util/Exceptional");
const EtudeParser_1 = require("../util/EtudeParser");
const InfiniteIterator_1 = require("../util/InfiniteIterator");
const StreamUtil_1 = require("../util/StreamUtil");
const Degree_1 = require("./Degree");
const Direction_1 = require("./Direction");
const Interval_1 = require("./Interval");
const Pitch_1 = require("./Pitch");
const Scale_1 = require("./Scale");
class Chord {
    constructor(parameter, quality, inversion = Chord.Inversion.ROOT) {
        if (parameter instanceof Pitch_1.default) {
            if (arguments.length < 2 || arguments.length > 3) {
                throw new Error("Invalid number of arguments for chord constructor");
            }
            this.pitches = Chord
                .builder()
                .setRoot(parameter)
                .add(quality)
                .setInversion(inversion)
                .build()
                .pitches;
        }
        else if (parameter instanceof Array) {
            this.pitches = parameter;
        }
    }
    static fromString(chordString) {
        return EtudeParser_1.default
            .of(chordString)
            .filter(c => c != null, EtudeError_1.default.forNull(Chord))
            .parse(s => {
            if (!s.includes("[") || !s.includes("]")) {
                return Exceptional_1.default.empty(EtudeError_1.default.forInvalid(Chord, s, "missing brackets that enclose pitches"));
            }
            if (!s.startsWith("[") || !s.endsWith("]")) {
                return Exceptional_1.default.empty(EtudeError_1.default.forInvalid(Chord, s, "contains extra information"));
            }
            const pitchesString = s.substring(1, s.length - 1);
            if (pitchesString.includes("[") || pitchesString.includes("]")) {
                return Exceptional_1.default.empty(EtudeError_1.default.forInvalid(Chord, s, "contains extra brackets"));
            }
            // Pitch[] pitches = Arrays
            //   .stream(pitchesString.split(","))
            //   .map(Pitch::fromString)
            //   .map(Exceptional::isPresent)
            //   .map(Exceptional::get)
            //   .toArray(Pitch[]::new);
            // traditional for loop because can't do generic array creation (Exceptional<Pitch>[])
            const pitchStrings = pitchesString.split(",");
            const pitches = new Pitch_1.default[pitchStrings.length];
            for (let i = 0; i < pitchStrings.length; ++i) {
                const pitch = Pitch_1.default.fromString(pitchStrings[i]);
                if (!pitch.isPresent()) {
                    return Exceptional_1.default.empty(EtudeError_1.default.forInvalid(Chord, s, pitch.getException().message));
                }
                pitches[i] = pitch.get();
            }
            return Exceptional_1.default.of(pitches);
        })
            .get(a => new Chord(a[0]));
    }
    toString() {
        return "[" + this.pitches.join(", ") + "]";
    }
    stream(direction = Direction_1.default.DEFAULT) {
        return StreamUtil_1.default.fromIterator(this.iterator(direction));
    }
    iterator(direction = Direction_1.default.DEFAULT) {
        const keys = this.pitches.map(p => p.getKey());
        const it = direction == Direction_1.default.ASCENDING
            ? CircularIterator_1.default.of(keys)
            : CircularIterator_1.default.of(keys).reverse();
        // skip first key because equal to initial pitch's (pitches[0]) key
        it.next();
        return InfiniteIterator_1.default.of(this.pitches[0], direction == Direction_1.default.ASCENDING
            ? previous => previous.getHigherPitch(it.next().value).get()
            : previous => previous.getLowerPitch(it.next().value).get());
    }
    getPitches() {
        return this.pitches.slice();
    }
    static builder() {
        return new Chord.Builder();
    }
}
(function (Chord) {
    class Inversion {
        constructor() {
            ++Inversion.size;
            Inversion._values.push(this);
        }
        static values() {
            return Inversion._values.slice();
        }
        ordinal() {
            return Inversion._values.indexOf(this);
        }
        static valueOf(inversionString) {
            let inversion = Inversion[inversionString];
            if (inversion instanceof Inversion) {
                return inversion;
            }
            throw EtudeError_1.default.forInvalid(Inversion, inversionString);
        }
        getValue() {
            return this.ordinal();
        }
        toString() {
            return Object.keys(Inversion).find(i => Inversion[i] === this);
        }
    }
    Inversion.size = 0;
    Inversion._values = [];
    Inversion.ROOT = new Inversion();
    Inversion.FIRST = new Inversion();
    Inversion.SECOND = new Inversion();
    Inversion.THIRD = new Inversion();
    Chord.Inversion = Inversion;
    class Quality {
        constructor(intervalPattern) {
            this.intervalPattern = intervalPattern;
        }
        getIntervalPattern() {
            return this.intervalPattern.slice();
        }
    }
    Quality.MAJOR = new Quality([
        new Interval_1.default(Interval_1.default.Quality.PERFECT, 1),
        new Interval_1.default(Interval_1.default.Quality.MAJOR, 3),
        new Interval_1.default(Interval_1.default.Quality.PERFECT, 5)
    ]);
    Quality.MINOR = new Quality([
        new Interval_1.default(Interval_1.default.Quality.PERFECT, 1),
        new Interval_1.default(Interval_1.default.Quality.MINOR, 3),
        new Interval_1.default(Interval_1.default.Quality.PERFECT, 5)
    ]);
    Quality.DIMINISHED = new Quality([
        new Interval_1.default(Interval_1.default.Quality.PERFECT, 1),
        new Interval_1.default(Interval_1.default.Quality.MINOR, 3),
        new Interval_1.default(Interval_1.default.Quality.DIMINISHED, 5)
    ]);
    Quality.AUGMENTED = new Quality([
        new Interval_1.default(Interval_1.default.Quality.PERFECT, 1),
        new Interval_1.default(Interval_1.default.Quality.MAJOR, 3),
        new Interval_1.default(Interval_1.default.Quality.AUGMENTED, 5)
    ]);
    Quality.MAJOR_SEVENTH = new Quality([
        new Interval_1.default(Interval_1.default.Quality.PERFECT, 1),
        new Interval_1.default(Interval_1.default.Quality.MAJOR, 3),
        new Interval_1.default(Interval_1.default.Quality.PERFECT, 5),
        new Interval_1.default(Interval_1.default.Quality.MAJOR, 7)
    ]);
    Quality.MINOR_SEVENTH = new Quality([
        new Interval_1.default(Interval_1.default.Quality.PERFECT, 1),
        new Interval_1.default(Interval_1.default.Quality.MINOR, 3),
        new Interval_1.default(Interval_1.default.Quality.PERFECT, 5),
        new Interval_1.default(Interval_1.default.Quality.MINOR, 7)
    ]);
    Quality.DOMINANT_SEVENTH = new Quality([
        new Interval_1.default(Interval_1.default.Quality.PERFECT, 1),
        new Interval_1.default(Interval_1.default.Quality.MAJOR, 3),
        new Interval_1.default(Interval_1.default.Quality.PERFECT, 5),
        new Interval_1.default(Interval_1.default.Quality.MINOR, 7)
    ]);
    Quality.DIMINISHED_SEVENTH = new Quality([
        new Interval_1.default(Interval_1.default.Quality.PERFECT, 1),
        new Interval_1.default(Interval_1.default.Quality.MINOR, 3),
        new Interval_1.default(Interval_1.default.Quality.DIMINISHED, 5),
        new Interval_1.default(Interval_1.default.Quality.DIMINISHED, 7)
    ]);
    Quality.HALF_DIMINISHED_SEVENTH = new Quality([
        new Interval_1.default(Interval_1.default.Quality.PERFECT, 1),
        new Interval_1.default(Interval_1.default.Quality.MINOR, 3),
        new Interval_1.default(Interval_1.default.Quality.DIMINISHED, 5),
        new Interval_1.default(Interval_1.default.Quality.MINOR, 7)
    ]);
    Quality.MINOR_MAJOR_SEVENTH = new Quality([
        new Interval_1.default(Interval_1.default.Quality.PERFECT, 1),
        new Interval_1.default(Interval_1.default.Quality.MINOR, 3),
        new Interval_1.default(Interval_1.default.Quality.PERFECT, 5),
        new Interval_1.default(Interval_1.default.Quality.MAJOR, 7)
    ]);
    Quality.AUGMENTED_MAJOR_SEVENTH = new Quality([
        new Interval_1.default(Interval_1.default.Quality.PERFECT, 1),
        new Interval_1.default(Interval_1.default.Quality.MAJOR, 3),
        new Interval_1.default(Interval_1.default.Quality.AUGMENTED, 5),
        new Interval_1.default(Interval_1.default.Quality.MAJOR, 7)
    ]);
    Chord.Quality = Quality;
    class Builder {
        constructor() {
            this.pitches = [];
            this.bottomDegree = Degree_1.default.TONIC;
        }
        setRoot(root) {
            this.root = root;
            return this;
        }
        add(element) {
            if (element instanceof Interval_1.default) {
                this.pitches.push(this.root.step(element).get());
            }
            else if (element instanceof Quality) {
                element
                    .getIntervalPattern()
                    .forEach(i => this.add(i));
            }
            return this;
        }
        setInversion(inversion) {
            switch (inversion) {
                case Inversion.ROOT: return this.setBottomDegree(Degree_1.default.TONIC);
                case Inversion.FIRST: return this.setBottomDegree(Degree_1.default.MEDIANT);
                case Inversion.SECOND: return this.setBottomDegree(Degree_1.default.DOMINANT);
                case Inversion.THIRD: return this.setBottomDegree(Degree_1.default.LEADING_TONE);
                default: throw new AssertionError_1.default("Invalid inversion: " + inversion);
            }
        }
        setBottomDegree(degree) {
            this.bottomDegree = degree;
            return this;
        }
        build() {
            const deque = this.pitches.slice();
            // arbitrary octave
            const scale = new Scale_1.default(new Pitch_1.default(this.root.getKey(), 4), Scale_1.default.Quality.MAJOR);
            const letter = scale.getPitches()[this.bottomDegree.getValue() - 1].getKey().getLetter();
            const value = deque
                .find(p => p.getKey().getLetter() == letter);
            const exceptional = Exceptional_1.default.ofNullable(value);
            if (!exceptional.isPresent()) {
                throw EtudeError_1.default.forIllegalState(Chord, "Unable to invert chord: missing " + this.bottomDegree + " pitch");
            }
            const lowestPitch = exceptional.get();
            // guaranteed to terminate because the exceptional above
            while (lowestPitch != deque[0]) {
                deque.push(lowestPitch.getHigherPitch(deque.shift().getKey()).get());
            }
            return new Chord(deque);
        }
    }
    Chord.Builder = Builder;
})(Chord || (Chord = {}));
exports.default = Chord;
